/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  ChargingStationAPIResponse,
  ChargingStationInitialProperties,
  ChargingStationInterFace,
  RequestChargingStationInterface,
} from '../../interface/charging-location.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import stationApi from '../../utils/api/charging-location';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch users
export const fetchAllStation = createAsyncThunk<
  ChargingStationAPIResponse,
  RequestChargingStationInterface
>(
  'location-management/fetchAllStation',
  async (data: RequestChargingStationInterface, { dispatch }) => {
    const response = await stationApi
      .fetchChargingStations(removeEmptyKeys({ ...data, submissionStatus: 'approved' }))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((stationData: ChargingStationInterFace) => ({
        ...stationData,
        date: stationData.createdAt ? ConvertUTCtoDate(stationData.createdAt) : '',
        latLong: `${stationData.latitude}, ${stationData.longitude}`,
        opensAt: stationData.opensAt ? FormatUtcString(ConvertUTCtoHHMM(stationData?.opensAt)) : '',
        closesAt: stationData.closesAt
          ? FormatUtcString(ConvertUTCtoHHMM(stationData?.closesAt))
          : '',
        id: stationData._id,
      })),
    };
  },
);

// a thunk to fetch devices filters
export const fetchApprovedStationManagementMethodFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('location-management/fetchApprovedStationManagementMethodFilters', async (_, { dispatch }) => {
  try {
    const response = await stationApi.fetchChargingStationsListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ChargingStationInitialProperties = {
  stations: [],
  selectedStationPoint: null,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const approvedStationSlice = createSlice({
  name: 'location-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateApprovedStationManagementFilters: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableApprovedStationManagementApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<ChargingStationInitialProperties>) => {
    builder
      .addCase(fetchAllStation.pending, (state: ChargingStationInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllStation.fulfilled,
        (
          state: ChargingStationInitialProperties,
          action: PayloadAction<ChargingStationAPIResponse>,
        ) => {
          console.log(action.payload);

          state.loading = false;
          state.stations = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllStation.rejected, (state: ChargingStationInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchApprovedStationManagementMethodFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchApprovedStationManagementMethodFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchApprovedStationManagementMethodFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  updateApprovedStationManagementFilters,
  disableApprovedStationManagementApplied,
  setSortData,
} = approvedStationSlice.actions;
// Export the reducer
export default approvedStationSlice.reducer;
